export const WEBSITE_URL = import.meta.env.VITE_WEBSITE_URL || 'https://www.givelegacy.com'

export const CRYO_STORAGE_LINK = getWebsiteLink('/sperm-freezing/')
export const CD_CRYO_STORAGE_LINK = 'buy-cryogenic'
export const ORDER_LINK = getWebsiteLink('/order/#product-comparison-table')
export const RESOURCES_LINK = getWebsiteLink('/resources')
export const CONTACT_LINK = getWebsiteLink('/contact')
export const HELP_CENTER_LINK = 'https://help.givelegacy.com/s/'
export const SPERM_ANALYSIS_LINK = getWebsiteLink('/sperm-analysis')
export const DNA_FRAGMENTATION_LEARN_MORE_LINK = getWebsiteLink(
	'/sperm-dna-fragmentation-analysis/',
)
export const BUY_FOR_TODAY_DNA_FRAGMENTATION_LINK = getWebsiteLink('/for-today-dna-fragmentation/')
export const SPERM_FREEZING_LINK = getWebsiteLink('/sperm-freezing/#packages-with-storage')
export const ADD_STORAGE_LINK = getWebsiteLink('/sperm-freezing/#cryo-storage')
// The affiliate partner pays Legacy against this exact tracking path.
export const FERTILITY_SUPPLEMENTS_LINK = 'https://alphasperm.sjv.io/9VqL10'
export const COMMUNITY_LINK = 'https://legacy.circle.so'
export const EVENT_LINK = 'https://legacy.circle.so/home'

export const GOOGLE_AUTHENTICATOR_LINK =
	'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en'
export const TYPEFORM_LINK =
	'https://admin.typeform.com/signup?utm_campaign=PyWfTz&utm_source=typeform.com-01DKCWX7XWJB29GHFDHGXZS761-professional&utm_medium=typeform&utm_content=typeform-embedded-poweredbytypeform&utm_term=EN'
export const ORDER_LINK_ONLY = getWebsiteLink('/order')
export const LEGACY_PHONE_LINK = 'tel:+16175140901'
export const CX_EMAIL = 'clientservices@givelegacy.com'
export const KIT_VIDEO_WALK_THROUGH_LINK =
	'https://player.vimeo.com/progressive_redirect/playback/838899568/rendition/720p/file.mp4?loc=external&log_user=0&signature=34d3a4f23f3be14c28be78e7705296a5fb1c0ecfeeb10e94e58bf56319deb1e5'
export const KIT_V3_VIDEO_WALK_THROUGH_LINK =
	'https://player.vimeo.com/progressive_redirect/playback/1101211122/rendition/720p/file.mp4?loc=external&log_user=0&signature=820f20cee9f9b11df977d14bf7d9428e6d5adc5c5729504c8bae1c45dde40c77'
export const FEDEX_LOCATIONS_LINK = 'https://www.fedex.com/en-us/shipping/ship-centers.html'
export const FEDEX_SCHEDULE_PICKUP_LINK =
	'https://www.fedex.com/en-us/shipping/schedule-manage-pickups.html'
export const UPS_PICKUP_LINK =
	'https://www.ups.com/us/en/business-solutions/pickup-dropoff-options/ups-on-call-pickup.page'
export const UPS_DROPBOX_LINK = 'https://www.ups.com/dropoff?loc=en_US'
export const CHECKOUTS_PATH = '/checkouts'

export function getWebsiteLink(path: string): string {
	return WEBSITE_URL + path
}

export function getLifestyleSurveyLink(userId: string | undefined): string {
	const typeformSurveyBaseUrl = import.meta.env.VITE_TYPEFORM_SURVEY_BASE_URL
	if (!userId) {
		return typeformSurveyBaseUrl
	}
	return `${typeformSurveyBaseUrl}?targetID=${userId}`
}

export function getCheckoutUrl(
	packageUuid: string,
	quantity: number,
	discountCode?: string,
): string {
	var checkoutUrl = WEBSITE_URL + '/cart?variant_ids=' + packageUuid

	// runs if quantity is greater than 1
	for (let i = 1; i < quantity; i++) {
		checkoutUrl += '&variant_ids=' + packageUuid
	}

	if (discountCode) {
		checkoutUrl += '&coupon_code=' + encodeURIComponent(discountCode)
	}

	return checkoutUrl
}

/** Details the storefront checkout form can be prefilled with. */
export interface CheckoutClientDetails {
	email?: string
	/** Date only, `YYYY-MM-DD`. */
	dob?: string
	phone?: string
	firstName?: string
	lastName?: string
}

export interface DirectCheckoutOptions {
	quantity?: number
	discountCode?: string
}

/**
 * Cart URL that starts a fresh cart holding just this product and lands the client
 * on checkout with their details already filled in.
 *
 * `redirect_path` carries a query string of its own, which the storefront only parses
 * when it's opened with `?` and separated with `*` — a literal `&` would close out the
 * outer param instead.
 */
export function getDirectCheckoutUrl(
	packageUuid: string,
	client: CheckoutClientDetails,
	options: DirectCheckoutOptions = {},
): string {
	const prefill = {
		client_email: client.email,
		client_dob: client.dob,
		client_phone_number: client.phone,
		client_first_name: client.firstName,
		client_last_name: client.lastName,
	}

	const redirectQuery = Object.entries(prefill)
		.map(([key, value]) => `${key}=${encodeURIComponent(value ?? '')}`)
		.join('*')

	return (
		getCheckoutUrl(packageUuid, options.quantity ?? 1, options.discountCode) +
		'&new_cart=true' +
		'&redirect_path=checkout?' +
		redirectQuery
	)
}

// Anchor and link are kept together so the page rendering one and the page building the other can't drift apart.
export function cryogenicStorageAnchor(analysisUuid: string): string {
	return `storage-${analysisUuid}`
}

export function cryogenicStorageLink(analysisUuid: string): string {
	return `/cryogenic#${cryogenicStorageAnchor(analysisUuid)}`
}

export function orderHistoryAnchor(poNumber: string | number): string {
	return `order-${poNumber}`
}

export function orderHistoryLink(poNumber: string | number): string {
	return `/orders/history#${orderHistoryAnchor(poNumber)}`
}

const LINKABLE_SCHEMES = ['http:', 'https:', 'mailto:', 'tel:']

function absoluteUrl(url: string): URL | null {
	try {
		return new URL(url, window.location.origin)
	} catch {
		return null
	}
}

/** False for a scheme an href must never carry — `javascript:` above all. */
export function isLinkableUrl(url: string): boolean {
	const parsed = absoluteUrl(url)

	return parsed !== null && LINKABLE_SCHEMES.includes(parsed.protocol)
}

/** The route to hand vue-router for a link that stays in the dashboard, or null when it leaves. */
export function dashboardRouteFor(url: string): string | null {
	const parsed = absoluteUrl(url)

	if (parsed === null || parsed.origin !== window.location.origin) {
		return null
	}

	return parsed.pathname + parsed.search + parsed.hash
}
